import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const { signIn, sendPasswordReset, setNewPassword, recovering } = useAuth();
  const [forgot, setForgot] = useState(false);
  const [notice, setNotice] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await signIn(email.trim(), password.trim());
    if (error) {
      setError('Invalid email or password. Contact your supervisor.');
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await sendPasswordReset(email.trim());
    setLoading(false);
    if (error) setError('Could not send the reset email. Check the address and try again.');
    else setNotice('If that address has an account, a reset link is on its way. Check your email.');
  }

  async function handleNewPassword(e) {
    e.preventDefault();
    if (password.length < 8) { setError('Use at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    const { error } = await setNewPassword(password);
    if (error) { setError('Could not save the new password. Ask for a fresh reset link.'); setLoading(false); }
  }

  if (recovering) {
    return (
      <div style={styles.screen}>
        <div style={styles.card}>
          <div style={styles.title}>Choose a new password</div>
          <div style={styles.subtitle}>Then you are signed in.</div>
          <form onSubmit={handleNewPassword} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>New Password</label>
              <input style={styles.input} type="password" value={password}
                onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
            </div>
            {error && <div style={styles.error}>{error}</div>}
            <button type="submit" style={loading ? { ...styles.btn, opacity: 0.6 } : styles.btn} disabled={loading}>
              {loading ? 'Saving...' : 'Save password →'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>TC</div>
          <div>
            <div style={styles.logoName}>TraverseClean</div>
            <div style={styles.logoSub}>Town of Paradise · TOP-25-27</div>
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.title}>Sign In</div>
        <div style={styles.subtitle}>Use the credentials provided by your supervisor</div>

        <form onSubmit={forgot ? handleForgot : handleLogin} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoComplete="email"
            />
          </div>

          {!forgot && (
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
          {notice && <div style={styles.notice}>{notice}</div>}

          <button
            type="submit"
            style={loading ? { ...styles.btn, opacity: 0.6 } : styles.btn}
            disabled={loading}
          >
            {forgot ? (loading ? 'Sending...' : 'Email me a reset link →') : (loading ? 'Signing in...' : 'Sign In →')}
          </button>
          <button
            type="button"
            style={styles.linkBtn}
            onClick={() => { setForgot(!forgot); setError(''); setNotice(''); }}
          >
            {forgot ? 'Back to sign in' : 'Forgot password?'}
          </button>
        </form>

        <div style={styles.footer}>
          Need access? Contact Fabian Traverse · (709) 771-2158
          <br />
          Trouble with the site? office@fabiantraverse.com
        </div>
      </div>
    </div>
  );
}

const styles = {
  screen: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: '#1e293b',
    borderRadius: 16,
    padding: '36px 32px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
    border: '1px solid #334155',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  logoMark: {
    background: '#f97316',
    color: 'white',
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 16,
    letterSpacing: -0.5,
    flexShrink: 0,
  },
  logoName: {
    color: 'white',
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: -0.5,
  },
  logoSub: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 1,
  },
  divider: {
    height: 1,
    background: '#334155',
    marginBottom: 24,
  },
  title: {
    color: 'white',
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 6,
  },
  subtitle: {
    color: '#64748b',
    fontSize: 13,
    marginBottom: 24,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    color: '#94a3b8',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    padding: '12px 14px',
    color: 'white',
    fontSize: 15,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  error: {
    background: '#450a0a',
    border: '1px solid #7f1d1d',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fca5a5',
    fontSize: 13,
  },
  notice: {
    background: '#052e16',
    border: '1px solid #166534',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#86efac',
    fontSize: 13,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 4,
  },
  btn: {
    background: '#f97316',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '14px',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 4,
    letterSpacing: -0.3,
  },
  footer: {
    color: '#475569',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid #1e293b',
  },
};
